<div class="page">
  <div class="page-head">
    <div>
      <h2>{{ pageTitle() || 'App Notifications' }}</h2>
      <p>Push notifications to the partner mobile app and reply to partner-reported issues.</p>
    </div>
    <div class="head-actions">
      @if (activeTab() === 'push') {
        <p-button label="Refresh" icon="pi pi-refresh" [outlined]="true" (onClick)="loadPushes()" />
      } @else {
        <p-button label="Refresh" icon="pi pi-refresh" [outlined]="true" (onClick)="loadIssues()" />
      }
    </div>
  </div>

  <div class="card tabs-card">
    <p-tabs [value]="activeTab()" (valueChange)="setTab($event)">
      <p-tablist>
        <p-tab value="push"><i class="pi pi-send"></i> Push Notifications</p-tab>
        <p-tab value="issues">
          <i class="pi pi-comments"></i> Partner Issues
          @if (unreadOpen() > 0) { <span class="tab-badge">{{ unreadOpen() }}</span> }
        </p-tab>
      </p-tablist>
      <p-tabpanels>
        <!-- ---------- Push Notifications ---------- -->
        <p-tabpanel value="push">
          <form [formGroup]="form" class="compose">
            <h3 class="block-title"><i class="pi pi-megaphone"></i> Compose</h3>
            <div class="compose-grid">
              <div class="field">
                <label>Send to <span class="req">*</span></label>
                <p-select formControlName="target" [options]="targetOptions()" optionLabel="label"
                  optionValue="value" [filter]="true" filterBy="label" appendTo="body"
                  [loading]="usersLoading()" placeholder="📢 All partners (broadcast)" />
              </div>
              <div class="field">
                <label>Title <span class="req">*</span></label>
                <input pInputText type="text" formControlName="title" maxlength="150"
                  placeholder="e.g. New premium rates are live">
                @if (form.get('title')?.touched && form.get('title')?.invalid) {
                  <small class="err">Title is required</small>
                }
              </div>
            </div>
            <div class="field">
              <label>Message <span class="req">*</span></label>
              <textarea pTextarea formControlName="body" rows="4" maxlength="2000"
                placeholder="The notification body shown in the partner app…"></textarea>
              <div class="body-meta">
                @if (form.get('body')?.touched && form.get('body')?.invalid) {
                  <small class="err">Message is required</small>
                } @else { <span></span> }
                <small class="hint-muted">{{ bodyLen() }}/2000</small>
              </div>
            </div>
            <div class="compose-actions">
              @if (fTarget() === null) {
                <small class="hint-muted broadcast-hint">
                  <i class="pi pi-megaphone"></i> Broadcast — every partner app user receives this.
                </small>
              } @else { <span></span> }
              <p-button [label]="sending() ? 'Sending...' : 'Send'" icon="pi pi-send" type="button"
                [loading]="sending()" [disabled]="sending()" (onClick)="send()" />
            </div>
          </form>

          <h3 class="block-title history-title"><i class="pi pi-history"></i> Sent history</h3>
          <p-table [value]="pushes()" [loading]="pushesLoading()" dataKey="id" [rows]="10"
            [paginator]="pushes().length > 10" [rowsPerPageOptions]="[10, 25, 50]" styleClass="p-datatable-sm"
            [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 170px">Sent</th>
                <th>Target</th>
                <th>Title</th>
                <th>Message</th>
                <th class="ta-r" style="width: 100px">Read</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-p>
              <tr>
                <td class="nowrap">{{ p.createdAt | indDate:true }}</td>
                <td>
                  @if (p.userId == null) {
                    <p-tag value="Broadcast" severity="info" />
                  } @else {
                    <span class="name">{{ p.targetUsername || ('User #' + p.userId) }}</span>
                  }
                </td>
                <td class="name">{{ p.title }}</td>
                <td class="snippet" [pTooltip]="p.body" tooltipPosition="top">{{ snippet(p.body) }}</td>
                <td class="ta-r nowrap">{{ p.readCount }} read</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="5">
                  <div class="state"><i class="pi pi-send"></i>
                    <p>Nothing sent yet. Compose the first notification above.</p>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </p-tabpanel>

        <!-- ---------- Partner Issues ---------- -->
        <p-tabpanel value="issues">
          <div class="filter-row">
            <p-select [ngModel]="statusFilter()" (ngModelChange)="onStatusChange($event)"
              [options]="statusOptions" optionLabel="label" optionValue="value" appendTo="body"
              styleClass="status-filter" placeholder="All statuses" />
            <span class="search-box">
              <i class="pi pi-search"></i>
              <input pInputText type="text" [ngModel]="searchText()" (ngModelChange)="onSearch($event)"
                placeholder="Search title / partner…">
            </span>
          </div>

          <p-table [value]="issues()" [loading]="issuesLoading()" dataKey="id" [rows]="10"
            [paginator]="issues().length > 10" [rowsPerPageOptions]="[10, 25, 50]" styleClass="p-datatable-sm"
            [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 110px">Status</th>
                <th>Partner</th>
                <th>Title</th>
                <th>Issue</th>
                <th class="ta-c" style="width: 90px">Replies</th>
                <th style="width: 170px">Reported</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-i>
              <tr class="issue-row" [class.unread]="!i.isReadByAdmin" (click)="openThread(i)"
                pTooltip="Open the thread" tooltipPosition="top">
                <td><p-tag [value]="i.status" [severity]="statusSeverity(i.status)" /></td>
                <td>
                  <span class="name">{{ i.partnerName || i.username }}</span>
                  @if (i.partnerCode) { <small class="sub-line">{{ i.partnerCode }}</small> }
                </td>
                <td class="name">{{ i.title }}</td>
                <td class="snippet">{{ snippet(i.body) }}</td>
                <td class="ta-c">{{ i.replyCount }}</td>
                <td class="nowrap">{{ i.createdAt | indDate:true }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="6">
                  <div class="state"><i class="pi pi-comments"></i>
                    <p>No partner issues{{ statusFilter() || searchText() ? ' match the filters' : ' reported yet' }}.</p>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </p-tabpanel>
      </p-tabpanels>
    </p-tabs>
  </div>
</div>

<!-- ===================== ISSUE THREAD DIALOG ===================== -->
<p-dialog [visible]="showThread()" (visibleChange)="onThreadVisibleChange($event)" [modal]="true"
  [draggable]="true" [resizable]="false" [dismissableMask]="true" header="Issue Thread"
  styleClass="menu-dialog" [style]="{ width: '640px' }" [breakpoints]="{ '700px': '94vw' }">
  @if (threadLoading()) {
    <div class="state"><i class="pi pi-spin pi-spinner"></i><p>Loading thread…</p></div>
  }
  @if (thread(); as t) {
    <div class="thread">
      <div class="issue-head">
        <div class="issue-head-top">
          <p-tag [value]="t.issue.status" [severity]="statusSeverity(t.issue.status)" />
          <span class="hint-muted">Filed {{ t.issue.createdAt | indDate:true }}</span>
        </div>
        <div class="partner-line">
          <strong>{{ t.issue.partnerName || t.issue.username }}</strong>
          @if (t.issue.partnerCode) { <span class="code-chip">{{ t.issue.partnerCode }}</span> }
        </div>
        <div class="contact-line">
          @if (t.issue.mobile) { <span><i class="pi pi-phone"></i> {{ t.issue.mobile }}</span> }
          @if (t.issue.email) { <span><i class="pi pi-envelope"></i> {{ t.issue.email }}</span> }
        </div>
      </div>

      <div class="issue-body">
        <h4>{{ t.issue.title }}</h4>
        <p>{{ t.issue.body }}</p>
      </div>

      @if (t.replies.length) {
        <div class="replies">
          @for (r of t.replies; track r.id) {
            <div class="reply-card" [class.reply-card--partner]="r.fromPartner">
              <div class="reply-meta">
                <strong>{{ r.fromPartner ? (t.issue.partnerName || 'Partner') : 'Support team' }}</strong>
                · {{ r.createdAt | indDate:true }}
              </div>
              <div class="reply-body">{{ r.body }}</div>
            </div>
          }
        </div>
      } @else {
        <p class="hint-muted no-replies">No replies yet.</p>
      }

      @if (t.issue.status !== 'CLOSED') {
        <div class="field">
          <label>Reply <span class="req">*</span></label>
          <textarea pTextarea rows="3" maxlength="2000" [ngModel]="replyText()"
            (ngModelChange)="replyText.set($event)"
            placeholder="Write a reply — the partner sees it in the app…"></textarea>
        </div>
        <div class="modal-actions">
          <p-button label="Close Issue" icon="pi pi-lock" severity="secondary" [outlined]="true" type="button"
            [loading]="closing()" [disabled]="closing() || replying()" (onClick)="closeIssue()" />
          <p-button [label]="replying() ? 'Sending...' : 'Send Reply'" icon="pi pi-send" type="button"
            [loading]="replying()" [disabled]="replying() || closing()" (onClick)="sendReply()" />
        </div>
      } @else {
        <div class="modal-actions">
          <p-button label="Done" [outlined]="true" type="button" (onClick)="closeThread()" />
        </div>
      }
    </div>
  }
</p-dialog>
